// The card an inactive player sees (spec 2026-10-22): the game turns grey and blurred under a
// "Przerwa" card until any click, tap or key. A DOM layer, not a Phaser object, so it sits over
// every scene, the round audio buttons (z 1000) and the phone shell (z 1500); the rotate prompt
// (z 5000) stays above it. The input rule lives in inactiveShield.mjs (unit-tested).
import { idleTracker } from '../state/idleTracker.js';
import { shieldStep, shieldIdle, SHIELD_EVENTS, SHIELD_REST } from './inactiveShield.mjs';

const STYLE_ID = 'inactive-overlay-style';
const CSS = `
#inactive-overlay { position:fixed; inset:0; z-index:4000; display:none; align-items:center;
  justify-content:center; background:rgba(40,30,20,.25); cursor:pointer; }
#inactive-overlay .io-card { width:min(420px, calc(100vw - 32px)); padding:22px 22px 26px;
  background:#fff3dc; border:4px solid #c59a3e; border-radius:22px; text-align:center;
  box-shadow:0 8px 0 #8d5a2b, 0 18px 40px rgba(0,0,0,.35); font-family:inherit; color:#5a3a12; }
#inactive-overlay h2 { margin:0 0 10px; font-size:30px; color:#8d5a2b; }
#inactive-overlay p { margin:0 0 20px; font-size:18px; line-height:1.4; }
#inactive-overlay .io-btn { display:inline-block; padding:12px 34px; border-radius:14px;
  background:#6cbf45; color:#fff; font-size:20px; font-weight:bold;
  box-shadow:0 5px 0 #3f8a25, inset 0 2px 0 #9be07a; text-shadow:0 1px 0 #2d6b19; }
`;
const CANVAS_FILTER = 'grayscale(1) blur(4px)';

let root = null;
let shield = SHIELD_REST;
let wired = false;
let unwireTimer = null;

function ensureRoot() {
  if (root) return root;
  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
  }
  root = document.createElement('div');
  root.id = 'inactive-overlay';
  const card = document.createElement('div');
  card.className = 'io-card';
  const h = document.createElement('h2');
  h.textContent = 'Przerwa';
  const p = document.createElement('p');
  p.textContent = 'Przez 5 minut nic się nie działo, więc inni gracze Cię teraz nie widzą.';
  const btn = document.createElement('div');
  btn.className = 'io-btn';
  btn.textContent = 'Wracam';
  card.append(h, p, btn);
  root.appendChild(card);
  document.body.appendChild(root);
  return root;
}

// Capture phase on window: runs before Phaser's window listeners and before any element's own.
function onEvent(e) {
  const r = shieldStep(shield, e.type, performance.now());
  shield = r.state;
  if (r.swallow) {
    e.stopImmediatePropagation();
    if (e.cancelable && !e.type.startsWith('key')) e.preventDefault();   // keys keep their browser defaults (reload, zoom)
  }
  if (r.wake) idleTracker.markActivity(Date.now(), { deliberate: true });   // main.js then hides us and reconnects
  scheduleUnwire();
}

// The listeners are non-passive (they must be able to cancel a touch), so they are on the window
// only while the card is up or its waking press is still being followed — never for the rest of the
// session, where they would make every touch scroll on a phone wait for the main thread.
function wire() {
  if (wired) return;
  wired = true;
  for (const type of SHIELD_EVENTS) window.addEventListener(type, onEvent, { capture: true, passive: false });
}

function scheduleUnwire() {
  clearTimeout(unwireTimer);
  if (!wired || shield.shown) return;
  const now = performance.now();
  if (shieldIdle(shield, now)) {
    wired = false;
    for (const type of SHIELD_EVENTS) window.removeEventListener(type, onEvent, { capture: true });
    return;
  }
  unwireTimer = setTimeout(scheduleUnwire, Math.max(50, Math.max(shield.pressUntil, shield.trailUntil) - now));
}

function canvas() { return document.querySelector('#game-root canvas'); }

export function showInactiveOverlay() {
  if (shield.shown) return;
  wire();
  ensureRoot().style.display = 'flex';
  shield = { ...shield, shown: true };
  const c = canvas();
  if (c) { c.style.transition = 'filter .6s'; c.style.filter = CANVAS_FILTER; }
}

export function hideInactiveOverlay() {
  if (!shield.shown) return;
  shield = { ...shield, shown: false };   // a waking press still being held stays shielded (inactiveShield.mjs)
  if (root) root.style.display = 'none';
  const c = canvas();
  if (c) c.style.filter = '';
  scheduleUnwire();
}
